import type { UnitUsageUnit } from '@nadoutong/contracts';
import { KNOWN_UNIT_USAGE_UNITS } from '@nadoutong/contracts';
import { isOneOf } from './isOneOf.ts';

/**
 * Tolerant reader for `usage_logs.tenant_cost_breakdown_json` as relayed on
 * `OpsSupportRequestRow` (#1654).
 *
 * The column carries a `UsageCostBreakdown` envelope whose `dimensions` array
 * is a discriminated union: token rows (`tokens` + `per_mtok_usd`) and, since
 * #1650, one `dimension: 'unit'` row per unit-metered charge. This parser
 * exists so the console can render the unit row WITHOUT trusting the blob:
 *
 *   - Token dimensions are deliberately dropped — the detail panel already
 *     shows the four token counters from their own columns, and #1654's
 *     non-goal is "token 流水的展示逻辑零变化".
 *   - A dimension this build does not recognize degrades to a generic entry
 *     carrying its stored money, so a future dimension type never blanks the
 *     page or silently vanishes from a billing investigation.
 *   - Malformed JSON, a missing column, or a non-breakdown shape all yield
 *     the EMPTY view: the page renders exactly its legacy detail panel.
 *
 * Money is surfaced verbatim from the stored `usd_micros` / `usd_cents`
 * projections; nothing here recomputes `quantity × rate` (the backend's
 * micros→cents floor projection is authoritative, #1650). The same verbatim
 * rule applies to `quantity`: any finite number is kept as stored — a
 * negative or fractional quantity is a data anomaly the operator must SEE,
 * not have rounded or hidden by the reader.
 *
 * `unpriced_dimensions` is also read, but only the PRICING-semantic
 * markers surface: they are the ONLY visible trace of a quarantined /
 * zero-cost charge, whose breakdown envelope carries `dimensions: []`. The
 * remaining token-path entries in that array (missing token dimension names,
 * legacy fallbacks) stay invisible — the legacy panel must render
 * byte-identical for ordinary token rows.
 *
 * Membership is EXACT. A suffixed forensic string
 * (`unit_price_missing:call:web-search`) is not a member and is dropped.
 * Every producer that records a loss must emit the bare member; a suffix
 * may ride alongside for queries, it may not replace the member.
 */

/**
 * The zero-charge markers from `buildZeroBreakdown` an operator must be able
 * to SEE.
 *
 * Two are unit-semantic (#1650). The third, `variant_price_missing` (#1937),
 * is on the TOKEN lane and was dropped by this parser until the gap it opened
 * was closed: a request whose SKU declares price tiers and whose tier has no
 * price row is DELIVERED, the upstream is PAID, and the event is quarantined
 * at $0. Left invisible here, that request looked exactly like an ordinary
 * free one on the only surface an operator has for a billing investigation —
 * ADR-012 §3's "loudly free" reduced to silently free, with the money already
 * spent. It is a pricing misconfiguration and it must be findable.
 *
 * The fourth, `unit_surcharges_malformed` (#2150), is the TOKEN-lane twin of
 * that silence: a malformed surcharge list is stripped so the token body
 * keeps its precise billable math, and this marker is the only evidence the
 * extra units evaporated. Without it the strip is #1651 M5 all over again.
 *
 * The gateway's route gates refuse such a request before any spend on both
 * lanes, so a row carrying this marker is evidence that something got past
 * them (a lagging projection, a spec that changed between settle and rating).
 * That is precisely when an operator needs to see it.
 */
export type UnpricedReason =
  | 'unit_price_missing'
  | 'unit_not_delivered'
  | 'variant_price_missing'
  | 'unit_surcharges_malformed';

const UNPRICED_REASONS: readonly UnpricedReason[] = [
  'unit_price_missing',
  'unit_not_delivered',
  'variant_price_missing',
  'unit_surcharges_malformed',
] as const;

export interface UnitBreakdownEntry {
  /** Narrowed unit kind, or null when the unit string is not a known kind. */
  readonly unit: UnitUsageUnit | null;
  /** The wire unit string, for rendering unknown kinds verbatim. */
  readonly rawUnit: string;
  readonly quantity: number;
  /** `''` means no variant. */
  readonly variantKey: string;
  readonly rateUsdMicrosPerUnit: number;
  readonly usdMicros: number | null;
  readonly usdCents: number | null;
}

export interface UnknownBreakdownEntry {
  /** The wire `dimension` string; `''` when it was not a string at all. */
  readonly dimension: string;
  readonly usdMicros: number | null;
  readonly usdCents: number | null;
}

export interface TenantCostBreakdownView {
  readonly unitEntries: readonly UnitBreakdownEntry[];
  readonly unknownEntries: readonly UnknownBreakdownEntry[];
  /** Pricing-semantic zero-charge markers; other token-path markers drop. */
  readonly unpricedReasons: readonly UnpricedReason[];
}

const EMPTY_VIEW: TenantCostBreakdownView = Object.freeze({
  unitEntries: Object.freeze([]),
  unknownEntries: Object.freeze([]),
  unpricedReasons: Object.freeze([]),
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function finiteNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function toUnitEntry(entry: Record<string, unknown>): UnitBreakdownEntry | null {
  const rawUnit = entry['unit'];
  const quantity = finiteNumberOrNull(entry['quantity']);
  const rate = finiteNumberOrNull(entry['rate_usd_micros_per_unit']);
  if (typeof rawUnit !== 'string' || quantity === null || rate === null) {
    return null;
  }
  const variantKey = entry['variant_key'];
  return {
    unit: isOneOf(rawUnit, KNOWN_UNIT_USAGE_UNITS) ? rawUnit : null,
    rawUnit,
    quantity,
    variantKey: typeof variantKey === 'string' ? variantKey : '',
    rateUsdMicrosPerUnit: rate,
    usdMicros: finiteNumberOrNull(entry['usd_micros']),
    usdCents: finiteNumberOrNull(entry['usd_cents']),
  };
}

function toUnknownEntry(entry: unknown): UnknownBreakdownEntry {
  if (!isRecord(entry)) {
    return { dimension: '', usdMicros: null, usdCents: null };
  }
  const dimension = entry['dimension'];
  return {
    dimension: typeof dimension === 'string' ? dimension : '',
    usdMicros: finiteNumberOrNull(entry['usd_micros']),
    usdCents: finiteNumberOrNull(entry['usd_cents']),
  };
}

/** True for the token dimension shape the legacy panel already covers. */
function isTokenDimension(entry: Record<string, unknown>): boolean {
  return (
    typeof entry['tokens'] === 'number' && typeof entry['per_mtok_usd'] === 'number'
  );
}

export function parseTenantCostBreakdown(
  json: string | null | undefined,
): TenantCostBreakdownView {
  if (typeof json !== 'string' || json.length === 0) {
    return EMPTY_VIEW;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return EMPTY_VIEW;
  }
  if (!isRecord(parsed) || !Array.isArray(parsed['dimensions'])) {
    return EMPTY_VIEW;
  }
  const unitEntries: UnitBreakdownEntry[] = [];
  const unknownEntries: UnknownBreakdownEntry[] = [];
  for (const entry of parsed['dimensions']) {
    if (isRecord(entry) && entry['dimension'] === 'unit') {
      const unitEntry = toUnitEntry(entry);
      if (unitEntry !== null) {
        unitEntries.push(unitEntry);
        continue;
      }
      unknownEntries.push(toUnknownEntry(entry));
      continue;
    }
    if (isRecord(entry) && isTokenDimension(entry)) {
      continue;
    }
    unknownEntries.push(toUnknownEntry(entry));
  }
  const rawUnpriced = parsed['unpriced_dimensions'];
  const unpricedReasons = Array.isArray(rawUnpriced)
    ? rawUnpriced.filter((marker): marker is UnpricedReason =>
        isOneOf(marker, UNPRICED_REASONS),
      )
    : [];
  if (
    unitEntries.length === 0 &&
    unknownEntries.length === 0 &&
    unpricedReasons.length === 0
  ) {
    return EMPTY_VIEW;
  }
  return {
    unitEntries: Object.freeze(unitEntries),
    unknownEntries: Object.freeze(unknownEntries),
    unpricedReasons: Object.freeze(unpricedReasons),
  };
}
